










































































































import {
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
  computed,
  onMounted,
} from '@vue/composition-api';
import {
  ConnectSpellingDiscoursePayload,
  Pagination,
  SearchSpellingResultRow,
  TextOccurrencesResponseRow,
} from '@oare/types';
import { DataTableHeader } from 'vuetify';
import sl from '@/serviceLocator';
import utils from '@/utils';

export interface ConnectSpellingOccurrenceObj {
  textInfo: TextOccurrencesResponseRow[];
  dictionaryInfo: SearchSpellingResultRow;
}

export default defineComponent({
  props: {
    getTexts: {
      type: Function as PropType<
        (
          uuid: string[],
          request: Pagination
        ) => Promise<TextOccurrencesResponseRow[]>
      >,
      required: true,
    },
    searchSpellings: {
      type: Function as PropType<
        (spelling: string) => Promise<SearchSpellingResultRow[]>
      >,
      required: true,
    },
    spelling: {
      type: String,
      required: true,
    },
    discourseUuid: {
      type: String,
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const actions = sl.get('globalActions');
    const server = sl.get('serverProxy');
    const store = sl.get('store');

    const dictionaryInfo = ref<SearchSpellingResultRow[]>([]);
    const spellingOccurrencesLength = ref(0);
    const connectSpellingOccurenceObjs = ref<ConnectSpellingOccurrenceObj[]>(
      []
    );
    const canConnectSpellings = computed(() =>
      store.hasPermission('CONNECT_SPELLING')
    );
    const connectSelections = ref<ConnectSpellingDiscoursePayload | null>(null);
    const viewingConfirmationDialog = ref(false);
    const viewingMainDialog = ref(true);
    const referencesLoading = ref(false);
    const tableOptions = ref({
      page: 1,
      itemsPerPage: 10,
    });

    const headers: Ref<DataTableHeader[]> = ref([
      {
        text: 'Word',
        value: 'word',
      },
      {
        text: 'Form',
        value: 'form',
      },
      {
        text: 'Parse Info',
        value: 'parseInfo',
      },
      {
        text: 'Context',
        value: 'context',
      },
      { text: 'Connect', value: 'connect' },
    ]);

    const connectSpellings = async () => {
      try {
        if (connectSelections.value) {
          await server.connectSpelling(connectSelections.value);
        }
        actions.showSnackbar('Successfully connected spelling to discourse');
      } catch (err) {
        actions.showErrorSnackbar(
          'Error disconnecting spelling(s). Please try again.',
          err as Error
        );
      } finally {
        closeConfirmationDialog();
      }
    };

    const openConfirmationDialog = () => {
      viewingMainDialog.value = false;
      viewingConfirmationDialog.value = true;
    };

    const closeConfirmationDialog = () => {
      viewingConfirmationDialog.value = false;
      viewingMainDialog.value = true;
      emit('finish');
    };

    const getReferences = async () => {
      try {
        referencesLoading.value = true;

        connectSpellingOccurenceObjs.value = await Promise.all(
          dictionaryInfo.value.map(async d => {
            const textOccurence = await props.getTexts([d.spellingUuid], {
              page: 1,
              limit: 1,
            });
            return {
              dictionaryInfo: d,
              textInfo: textOccurence,
            };
          })
        );
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to load text occurrences',
          err as Error
        );
      } finally {
        referencesLoading.value = false;
      }
    };

    watch(
      () => props.spelling,
      async () => {
        await getReferences();
      }
    );

    watch(tableOptions, getReferences);

    onMounted(async () => {
      try {
        dictionaryInfo.value = await props.searchSpellings(props.spelling);
        spellingOccurrencesLength.value = dictionaryInfo.value.length;
        await getReferences();
      } catch (err) {
        actions.showErrorSnackbar('Failed on mount', err as Error);
      }
    });

    return {
      utils,
      spellingOccurrencesLength,
      headers,
      referencesLoading,
      tableOptions,
      connectSpellingOccurenceObjs,
      connectSelections,
      connectSpellings,
      canConnectSpellings,
      openConfirmationDialog,
      closeConfirmationDialog,
      viewingConfirmationDialog,
      viewingMainDialog,
    };
  },
});
